import React, { useState } from "react";
import { api } from "../../api";
import { motion } from "framer-motion";

export default function DailyStatsPage() {
  const [date, setDate] = useState("");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ New states for card reset
  const [cardNumber, setCardNumber] = useState("");
  const [cardError, setCardError] = useState("");
  const [cardLoading, setCardLoading] = useState(false);
  const [cardSuccess, setCardSuccess] = useState("");

  const [roomId, setRoomId] = useState("");
  const [roomRestId, setRoomRestId] = useState("");
  const [timerLoading, setTimerLoading] = useState(false);
  const [timerError, setTimerError] = useState("");
  const [timerSuccess, setTimerSuccess] = useState("");

  const fetchStats = async () => {
    if (!date) {
      setError("Please select a date");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const res = await api.get(`/daily-game/${date}`);
      setStats(res.data.game);
    } catch (err) {
      setStats(null);
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

// ✅ Function to reset card
const resetCard = async () => {
  if (!cardNumber) {
    setCardError("Please enter a card number");
    return;
  }

  if (!roomId) {
    setCardError("Please select a room");
    return;
  }

  setCardError("");
  setCardLoading(true);
  setCardSuccess("");

  try {
    const res = await api.put(
      `/api/cards/${cardNumber}/status?roomId=${roomId}`,
      { status: "available" }
    );

    setCardSuccess(res.data.message);
    setCardNumber("");
  } catch (err) {
    setCardError(err.response?.data?.error || "Failed to reset card");
  } finally {
    setCardLoading(false);
  }
};


  // 🚨 Admin emergency start room timer
  const startRoomTimer = async () => {
    if (!roomRestId) {
      setTimerError("Please enter a room ID");
      return;
    }

    setTimerError("");
    setTimerSuccess("");
    setTimerLoading(true);

    try {
      const res = await api.put(`/api/rooms/${roomRestId}/start-timer`);
      setTimerSuccess(res.data.message);
      setRoomRestId("");
    } catch (err) {
      setTimerError(err.response?.data?.error || "Failed to start timer");
    } finally {
      setTimerLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b253d] px-4 py-10">
      {/* PAGE TITLE */}
      <h1 className="text-center text-3xl sm:text-4xl text-white font-serif mb-8">
        Daily Game Statistics
      </h1>

      {/* SEARCH BOX */}
      <div className="max-w-lg mx-auto bg-[#12385d] p-6 rounded-xl shadow-lg">
        <label className="text-gray-200 font-serif mb-2 block">
          Select Date
        </label>

        <input
          type="date"
          value={date}
          onChange={(e) => {
            const selected = new Date(e.target.value);
            const yyyy = selected.getFullYear();
            const mm = String(selected.getMonth() + 1).padStart(2, "0");
            const dd = String(selected.getDate()).padStart(2, "0");
            setDate(`${yyyy}-${mm}-${dd}`);
          }}
          className="w-full bg-[#0e2b48] border border-[#2c4e74] text-white px-3 py-3 
            rounded-md focus:ring-2 focus:ring-blue-400 outline-none"
        />

        <button
          onClick={fetchStats}
          disabled={loading}
          className="w-full mt-4 bg-gradient-to-r from-[#4e6d8c] to-[#6784a4] 
          hover:opacity-90 text-white py-3 rounded-md font-semibold transition"
        >
          {loading ? "Searching..." : "Search"}
        </button>

        {error && <p className="text-red-400 text-center mt-3">{error}</p>}
      </div>

      {/* CARD RESET */}
      <div className="max-w-lg mx-auto bg-[#12385d] p-6 rounded-xl shadow-lg mt-6">
        <label className="text-gray-200 font-serif mb-2 block">
          Reset Card
        </label>
        <select
  value={roomId}
  onChange={(e) => setRoomId(e.target.value)}
  className="w-full mb-3 bg-[#0e2b48] border border-[#2c4e74] text-white px-3 py-3 
    rounded-md focus:ring-2 focus:ring-blue-400 outline-none"
>
  <option value="">Select Room</option>
  <option value="room1">ROOM_1</option>
  <option value="room2">ROOM_2</option>
  <option value="room3">ROOM_3</option>
  <option value="room4">ROOM_4</option>
  <option value="room5">ROOM_5</option>
  <option value="room6">ROOM_6</option>
  <option value="room7">ROOM_7</option>
  <option value="room8">ROOM_8</option>
  <option value="room9">ROOM_9</option>
</select>
        <div className="flex gap-2">
          <input
            type="number"
            placeholder="Card Number"
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value)}
            className="flex-1 bg-[#0e2b48] border border-[#2c4e74] text-white px-3 py-3 
              rounded-md focus:ring-2 focus:ring-blue-400 outline-none"
          />
          <button
            onClick={resetCard}
            disabled={cardLoading}
            className="bg-gradient-to-r from-[#4e6d8c] to-[#6784a4] 
              hover:opacity-90 text-white px-4 py-3 rounded-md font-semibold transition"
          >
            {cardLoading ? "Resetting..." : "Reset"}
          </button>
        </div>
        {cardError && <p className="text-red-400 mt-2">{cardError}</p>}
        {cardSuccess && <p className="text-green-400 mt-2">{cardSuccess}</p>}
      </div>
      {/* 🚨 EMERGENCY START TIMER */}
      <div className="max-w-lg mx-auto bg-[#12385d] p-6 rounded-xl shadow-lg mt-6">
        <label className="text-gray-200 font-serif mb-2 block">
          Emergency Start Room Timer
        </label>

        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Room ID"
            value={roomRestId}
            onChange={(e) => setRoomRestId(e.target.value)}
            className="flex-1 bg-[#0e2b48] border border-[#2c4e74] text-white px-3 py-3 
        rounded-md focus:ring-2 focus:ring-yellow-400 outline-none"
          />

          <button
            onClick={startRoomTimer}
            disabled={timerLoading}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 
        hover:opacity-90 text-black px-4 py-3 rounded-md font-bold transition"
          >
            {timerLoading ? "Starting..." : "Start"}
          </button>
        </div>

        {timerError && <p className="text-red-400 mt-2">{timerError}</p>}
        {timerSuccess && <p className="text-green-400 mt-2">{timerSuccess}</p>}
      </div>

      {/* STAT CARDS */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#12385d] p-6 rounded-xl shadow-lg text-center text-white"
          >
            <h3 className="text-xl font-serif mb-2">Total Games</h3>
            <p className="text-3xl font-bold">{stats.totalGames}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#12385d] p-6 rounded-xl shadow-lg text-center text-white"
          >
            <h3 className="text-xl font-serif mb-2">Total Win Amount</h3>
            <p className="text-3xl font-bold">{stats.totalWinAmount} Br</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#12385d] p-6 rounded-xl shadow-lg text-center text-white"
          >
            <h3 className="text-xl font-serif mb-2">Total Commission</h3>
            <p className="text-3xl font-bold">{stats.totalCommission} Br</p>
          </motion.div>
        </div>
      )}
    </div>
  );
}
